import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetch3, fetch4, fetch5 } from "../commonApis/commonApis";
import { addProductManifest, setProductManifestList } from "../features/productManifest/productManifestSlice";
import { useDispatch } from "react-redux";
const url = process.env.REACT_APP_BASE_URL;

interface ApiResponse {
  success: boolean;
  data: any;
  message?: string;
}

export const useProductManifestCreateMutation = () => {
  const dispatch = useDispatch();
  return useMutation({
    mutationFn: async (payload: any) => {
      const res: ApiResponse = await fetch4(
        `${url}/api/product-manifest/`,
        payload
      );

      if (!res.success) {
        throw res.message || "Something went wrong";
      }

      return res.data;
    },
    onSuccess: (data: any) => {
      dispatch(addProductManifest(data));
      console.log("✅ Product Manifest Created successfully:", data);
    },
    onError: (error: any) => {
      console.error(
        "❌ Product Manifest create request failed:",
        error.message
      );
    },
  });
};

// List Fatch view Data
export const useFetchProductManifestViewData = (id?: string) => {
  return useQuery({
    queryKey: ["itemProductViewData", id],
    queryFn: async () => {
      if (!id) return null; // skip if no id
      const res = await fetch3(`${url}/api/product-manifest/${id}`, "get");

      if (!res || !res.data) {
        return res.message;
      }

      return res.data;
    },
    enabled: !!id, // 🔥 ensures query only runs if id exists
    staleTime: 0,
  });
};

// List Fatch Data
export const useFetchProductManifestData = (
  page: number,
  limit: number,
  searchFor: string
) => {
  const dispatch = useDispatch();
  return useQuery({
    queryKey: ["itemProductData", page, limit, searchFor],
    queryFn: async () => {
      const res = await fetch3(
        `${url}/api/product-manifest?page=${page}&limit=${limit}&searchFor=${searchFor}&type=LM`,
        "get"
      );

      if (!res || !res.data) {
        return res.message;
      }
      dispatch(setProductManifestList(res.data));

      return res.data;
    },
    staleTime: 1000 * 60 * 5,
  });
};

// Status Update
interface UpdateItemPromoStatusPayload {
  id: string;
  status: any;
}

export const useUpdateProductManifestStatus = () => {
  return useMutation({
    mutationFn: async ({ id, status }: UpdateItemPromoStatusPayload) => {
      const res: ApiResponse = await fetch5(
        `${url}/api/product-manifest/${id}/status`,
        "PATCH",
        {
          status,
        }
      );

      if (!res.success) {
        throw new Error(res.message || "Failed to update vehicle status");
      }

      return res.data;
    },
    onSuccess: (data: any) => {
      console.log("✅ Promo status updated successfully:", data);
    },
    onError: (error: any) => {
      console.error("❌ Promo status update failed:", error.message);
    },
  });
};

export const useUpdateProductManifestAdminConfirm = (id: string) => {
  return useMutation({
    mutationFn: async (payload: any) => {
      const res: ApiResponse = await fetch5(
        `${url}/api/product-manifest/${id}/admin-confirm`,
        'PUT',
        payload
      );

      if (!res.success) {
        throw res.message || "Something went wrong";
      }

      return res.data;
    },
    onSuccess: (data: any) => {
      console.log("✅ Product Manifest updated successfully:", data);
    },
    onError: (error: any) => {
      console.error("❌ Product Manifest update failed:", error.message);
    },
  });
};

export const useAssignStaffProductManifest = (id: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: any) => {
      const res: ApiResponse = await fetch5(
        `${url}/api/product-manifest/${id}`,
        'PATCH',
        payload
      );

      if (!res.success) {
        throw res.message || "Something went wrong";
      }

      return res.data;
    },
    onSuccess: (data: any) => {
      console.log("✅ Assign Staff Product Manifest successfully:", data);
      queryClient.invalidateQueries({ queryKey: ["itemProductViewData", id] });
      queryClient.invalidateQueries({ queryKey: ["itemProductData"] });
    },
    onError: (error: any) => {
      console.error("❌ Product Manifest Assign Staff failed:", error.message);
    },
  });
};

export const useAssignInventoryStaffProductManifest = (id: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: any) => {
      const res: ApiResponse = await fetch5(
        `${url}/api/product-manifest/${id}/assign-inventory-staff`,
        'PATCH',
        payload
      );

      if (!res.success) {
        throw res.message || "Something went wrong";
      }

      return res.data;
    },
    onSuccess: (data: any) => {
      console.log("✅ Assign Inventory Staff Product Manifest successfully:", data);
      queryClient.invalidateQueries({ queryKey: ["itemProductViewData", id] });
      queryClient.invalidateQueries({ queryKey: ["itemProductData"] });
    },
    onError: (error: any) => {
      console.error("❌ Product Manifest Assign Inventory Staff failed:", error.message);
    },
  });
};

export const useProductManifestAddInventoryMutation = (id: string) => {
  const dispatch = useDispatch();
  return useMutation({
    mutationFn: async (payload: any) => {
      const res: ApiResponse = await fetch4(
        `${url}/api/product-movement/${id}`,
        payload
      );

      if (!res.success) {
        throw res.message || "Something went wrong";
      }

      return res.data;
    },
    onSuccess: (data: any) => {
      dispatch(addProductManifest(data));
      console.log("✅Product Manifest Inventory Created successfully:", data);
    },
    onError: (error: any) => {
      console.error(
        "❌ Product Manifest Inventory create request failed:",
        error.message
      );
    },
  });
};

export const useFetchProductItemInventoryData = (
  page: number,
  limit: number,
  searchFor: string,
  isReserved?: boolean,
  warehouseId?: string
) => {
  const dispatch = useDispatch();
  const reservedParam =
    isReserved !== undefined ? `&is_reserved=${isReserved}` : "";
  const warehouseParam = warehouseId ? `&warehouseId=${warehouseId}` : "";
  return useQuery({
    queryKey: ["itemInventoriesData", page, limit, searchFor, isReserved, warehouseId],
    queryFn: async () => {
      const res = await fetch3(
        `${url}/api/product-items-inventory?page=${page}&limit=${limit}&searchFor=${searchFor}&type=LM${reservedParam}${warehouseParam}`,
        "get"
      );

      if (!res || !res.data) {
        return res.message;
      }
      // dispatch(setProductList(res.data));
      return res.data;
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const useFetchLocationInventoryData = (id?: string) => {
  return useQuery({
    queryKey: ["fetchLocationInventoryData", id],
    queryFn: async () => {
      if (!id) return null; // skip if no id
      const res = await fetch3(`${url}/api/product-items-inventory/${id}`, "get");

      if (!res || !res.data) {
        return res.message;
      }

      return res.data;
    },
    enabled: !!id, // 🔥 ensures query only runs if id exists
    staleTime: 1000 * 60 * 5,
  });
};


export const useFetchProductItemsMovementData = (
  page: number,
  limit: number,
  searchFor: string
) => {
  const dispatch = useDispatch();
  return useQuery({
    queryKey: ["productItemsMovementData", page, limit, searchFor],
    queryFn: async () => {
      const res = await fetch3(
        `${url}/api/product-movement?page=${page}&limit=${limit}&searchFor=${searchFor}&type=LM`,
        "get"
      );

      if (!res || !res.data) {
        return res.message;
      }
      // dispatch(setProductList(res.data));
      return res.data;
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const useAddProductsToManifest = (id: string) => {
  return useMutation({
    mutationFn: async (payload: any) => {
      const res: ApiResponse = await fetch4(
        `${url}/api/product-manifest/${id}/add-products`,
        payload
      );

      if (!res.success) {
        throw new Error(res.message || "Failed to add products to manifest");
      }

      return res.data;
    },
    onSuccess: (data: any) => {
      console.log("Products added to manifest successfully:", data);
    },
    onError: (error: any) => {
      console.error("Failed to add products to manifest:", error.message);
    },
  });
};

export const useFetchMerchantLocations = (id?: string, inventoryType: string = "received") => {
  return useQuery({
    queryKey: ["fetchMerchantLocation", id, inventoryType],
    queryFn: async () => {
      if (!id) return null;
      const res = await fetch3(`${url}/api/product-manifest/${id}/item-locations?inventoryType=${inventoryType}`, "get");

      if (!res || !res.data) {
        return res.message;
      }

      return res.data;
    },
    enabled: !!id,
    staleTime: 1000 * 60 * 5,
  });
};